import type { Map as MlMap, Popup } from 'maplibre-gl'
import { Popup as MlPopup } from 'maplibre-gl'
import type { VehicleFlag } from '../lib/riskFlags'
import type { RouteProgress, TimsTrip, Vehicle, VehicleMeta } from '../lib/types'
import { estimateRange, withCorridorForecast } from '../lib/range'
import { loadLabel } from './layers/vehicles'
import { attachNetradyneCameraHandlers, netradyneCameraButtonHtml } from './netradyneCameraPopup'

/**
 * Anchored map chip: risk summary + range. Selecting the truck still opens the drawer.
 */
export function showVehiclePopup(
  map: MlMap,
  vehicleId: string,
  lngLat: [number, number],
  opts: {
    vehicle?: Vehicle
    meta?: VehicleMeta
    trip?: TimsTrip
    progress?: RouteProgress
    flag?: VehicleFlag
  },
  existing?: Popup | null,
): Popup {
  existing?.remove()
  const v = opts.vehicle
  const estimated = v ? estimateRange(v, opts.meta, opts.progress, undefined, opts.trip) : null
  const range = estimated && v
    ? withCorridorForecast(v, estimated, opts.progress)
    : estimated
  const load = loadLabel(opts.meta, opts.trip, opts.progress)
  const driver = opts.trip?.driver_name || opts.meta?.driver_name
  const lines: string[] = [
    `<div style="font-family:ui-sans-serif,system-ui;min-width:200px;max-width:260px">`,
    `<div style="font-weight:700;font-size:13px;letter-spacing:0.02em">${esc(vehicleId)}</div>`,
  ]
  if (driver) {
    lines.push(`<div style="margin-top:2px;font-size:11px;color:#334155">${esc(driver)}</div>`)
  }
  if (opts.trip?.lr_number || opts.trip?.event || opts.trip?.trailer) {
    const bits: string[] = []
    if (opts.trip.lr_number) bits.push(`LR ${opts.trip.lr_number}`)
    if (opts.trip.trailer) bits.push(opts.trip.trailer)
    if (opts.trip.event) bits.push(opts.trip.event)
    lines.push(`<div style="margin-top:2px;font-size:11px;color:#64748B">${esc(bits.join(' · '))}</div>`)
  }
  if (load) {
    lines.push(
      `<div style="margin-top:2px;font-size:11px;color:#64748B">${esc(load)}`
      + `${opts.progress?.next_hub ? ` · next ${esc(opts.progress.next_hub)}` : ''}</div>`,
    )
  }
  if (opts.flag) {
    const color = opts.flag.severity === 'critical' ? '#DC2626' : '#D97706'
    lines.push(
      `<div style="margin-top:8px;padding:6px 8px;border-radius:8px;background:${color}18;`
      + `border:1px solid ${color}55;color:${color};font-size:12px;font-weight:600">`
      + `${esc(opts.flag.label)} · ${esc(opts.flag.detail)}</div>`,
    )
  }
  if (v) {
    lines.push(
      `<div style="margin-top:8px;display:grid;grid-template-columns:1fr 1fr;gap:4px 10px;`
      + `font-size:11px;color:#334155">`,
      `<span>Speed</span><span style="text-align:right;font-variant-numeric:tabular-nums">`
      + `${v.speed_kph != null ? `${Math.round(v.speed_kph)} KMPH` : '—'}</span>`,
      `<span>SoC now</span><span style="text-align:right;font-variant-numeric:tabular-nums">`
      + `${v.soc_pct != null ? `${v.soc_pct}%` : '—'}</span>`,
    )
    if (range?.expected_soc_pct != null) {
      const tone = range.range_risk ? '#DC2626' : '#334155'
      lines.push(
        `<span>Exp. SoC</span><span style="text-align:right;font-variant-numeric:tabular-nums;color:${tone}">`
        + `${range.expected_soc_pct}%`
        + `${range.km_remaining != null ? ` @ ${Math.round(range.km_remaining)} km` : ''}</span>`,
      )
    }
    // Stated separately, because "0%" on its own does not say the truck stops before it
    // arrives -- and that is the whole point of the row.
    if (range?.shortfall_km != null) {
      lines.push(
        `<span>Short by</span><span style="text-align:right;font-variant-numeric:tabular-nums;color:#DC2626">`
        + `${range.shortfall_km} km</span>`,
      )
    }
    if (range?.est_range_km != null) {
      lines.push(
        `<span>Range left</span><span style="text-align:right;font-variant-numeric:tabular-nums">`
        + `~${range.est_range_km} km</span>`,
      )
    }
    if (range?.kwh_per_km != null) {
      lines.push(
        `<span>kWh/km</span><span style="text-align:right;font-variant-numeric:tabular-nums">`
        + `${range.kwh_per_km} (${range.kwh_per_km_source ?? '?'})</span>`,
      )
    }
    lines.push(`</div>`)
  }
  if (opts.flag?.alerts.length) {
    lines.push(`<div style="margin-top:8px;font-size:10px;color:#64748B">Recent</div><ul style="margin:4px 0 0;padding-left:16px;font-size:11px;color:#0F172A">`)
    for (const a of opts.flag.alerts.slice(0, 4)) {
      lines.push(`<li>${esc(a.kind)}</li>`)
    }
    lines.push(`</ul>`)
  }
  lines.push(
    netradyneCameraButtonHtml(),
    `<div style="margin-top:8px;font-size:10px;color:#94A3B8">Click truck for full drawer</div>`,
    `</div>`,
  )

  const popup = new MlPopup({
    closeButton: true,
    closeOnClick: true,
    maxWidth: '280px',
    offset: 28,
    className: 'fleet-vehicle-popup',
  })
    .setLngLat(lngLat)
    .setHTML(lines.join(''))
    .addTo(map)
  const el = popup.getElement()
  if (el) attachNetradyneCameraHandlers(el, 'ultratech', vehicleId)
  return popup
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}
